/** Search Bar
 *
 * Can be used in two ways:
 *
 * NORMAL -  Where you provide a value and a onChange handler that receives the change event and you can imediately updates the search value
 * DEBOUNCED - Where you provide the value and its setter (setValue). The value change is debounced. (You must provide isDebounced value)
 */

import {
  Dispatch,
  FocusEventHandler,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash";
import { X } from "lucide-react";
import { NoxusFormLabel } from "./Form/NoxusFormLabel";
import { Kbd } from "@/components/shadcn/kbd";
import { isMac } from "@/utils/generic";
import { Command, CommandInput } from "@/components/shadcn/command";

interface ISearchBarProps {
  value?: string;
  placeholder?: string;
  label?: string;
  tooltip?: string;
  isDebounced?: boolean;
  setValue?: Dispatch<SetStateAction<string>> | ((value: string) => void);
  height?: string | number | (string | number)[];
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  searchInputRef?: RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  showKeyBind?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
}

export const filterObjBasedOnSearch = (
  array: any[],
  value: string,
  properties: string[],
  sortByProperty: boolean = false
) => {
  const filteredArray = array.filter((f) =>
    properties.some((property: string) =>
      f?.[property]?.toLowerCase().includes(value.toLowerCase())
    )
  );

  return sortByProperty
    ? filteredArray.sort((a, b) => {
        for (const prop of properties) {
          const aIncludes = a[prop]
            ?.toLowerCase()
            .includes(value.toLowerCase());
          const bIncludes = b[prop]
            ?.toLowerCase()
            .includes(value.toLowerCase());

          if (aIncludes && !bIncludes) return -1; // a comes first
          if (!aIncludes && bIncludes) return 1; // b comes first
          // If both include or both don't, continue to the next property
        }
        return 0; // If all properties are equal in terms of match
      })
    : filteredArray;
};

export function SearchBar({
  value,
  label,
  tooltip,
  placeholder = "Search",
  isDebounced,
  setValue,
  onBlur,
  onFocus,
  searchInputRef,
  autoFocus,
  height,
  showKeyBind,
  isDisabled,
  isClearable,
}: ISearchBarProps) {
  const [inputValue, setInputValue] = useState<string>(value ?? "");
  const [prevValue, setPrevValue] = useState<string | undefined>(value);

  // Sync external value changes during render (no useEffect needed)
  if (value !== prevValue) {
    setPrevValue(value);
    if (value !== undefined) {
      setInputValue(value);
    }
  }

  // Callers often pass an inline setter; recreating the debounce on every
  // render would leave stale timers that later fire with a partial value.
  const setValueRef = useRef(setValue);
  useLayoutEffect(() => {
    setValueRef.current = setValue;
  });

  const debouncedSetSearchValue = useMemo(
    () => debounce((value: string) => setValueRef.current?.(value), 500),
    []
  );

  useEffect(
    () => () => debouncedSetSearchValue.cancel(),
    [debouncedSetSearchValue]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      debouncedSetSearchValue(value);
    },
    [debouncedSetSearchValue]
  );

  const handleNonDebouncedChange = useCallback(
    (val: string) => {
      setInputValue(val);
      setValue?.(val);
    },
    [setValue]
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    debouncedSetSearchValue.cancel();
    setValue?.("");
    searchInputRef?.current?.focus();
  }, [debouncedSetSearchValue, setValue, searchInputRef]);

  const isInputFocused = searchInputRef?.current?.contains(
    document.activeElement
  );

  return (
    <div className="w-full relative">
      <div className="w-full">
        {label != undefined && (
          <NoxusFormLabel label={label} tooltip={tooltip} />
        )}

        <Command
          variant="search"
          className="bg-background border border-input shadow-xs focus-within:ring-ring focus-within:ring-1 focus-within:outline-none"
        >
          <CommandInput
            className={`bg-background ${isClearable ? "pr-5" : ""}`}
            ref={searchInputRef}
            placeholder={placeholder ? placeholder : "Search"}
            value={inputValue}
            onValueChange={
              isDebounced ? handleInputChange : handleNonDebouncedChange
            }
            onBlur={onBlur}
            onFocus={onFocus}
            autoFocus={autoFocus}
            disabled={isDisabled}
          />
          {isClearable && inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </Command>
      </div>
      {showKeyBind && (
        <div
          className={`absolute right-1rem h-full flex items-center bottom-0 transition-opacity ${isInputFocused ? "opacity-0" : "opacity-1"}`}
        >
          <Kbd variant="hint">{isMac() ? "⌘ + F" : "Ctrl + F"}</Kbd>
        </div>
      )}
    </div>
  );
}
